import {
  Paging,
  Table,
  usePaging,
  useAsync,
  CustomFilter,
  Input,
  Form,
  Modal,
  useUpdate,
  EllipsisSpan,
} from '@maxtropy/components';
import {
  apiV2WaterFlowConfigTreePost,
  apiV2DevicePagePost,
  V2WaterFlowConfigTreePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { Cascader } from 'antd';
import { useQuery } from '@/utils/utils';

import { DeviceListItem } from './index';
import { DeviceTypeTree, formatOptionData, getChildNodesByParentIds, adaptToDeviceTypeTree } from './utils';

interface searchParams {
  codeOrName?: string;
  typeId?: Array<Array<number>> | undefined;
}
interface Params {
  codeOrName?: string;
  typeId?: number[];
}

const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v?: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

type modalProps = {
  open: boolean;
  cancelModal: () => void;
  ouIds?: number[];
  onSubmit: (allSelectedRows: DeviceListItem[]) => void;
};

const getValidSelectedRows = (rows?: Array<DeviceListItem | undefined>) =>
  (rows ?? []).filter((item): item is DeviceListItem => item?.id !== undefined && item.id !== null);

const UnitModal = ({ open, cancelModal, ouIds = [], onSubmit }: modalProps) => {
  const pagingInfo = usePaging();
  const [form] = Form.useForm();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>();
  const [selectedRow, setSelectedRow] = useState<DeviceListItem[]>([]);
  const [searchParams, setSearchParams] = useState<Params>();
  const initialSelectedSet = new Set(ouIds.map(id => id));

  const { data: rawData } = useRequest(() => apiV2WaterFlowConfigTreePost({}), { refreshDeps: [] });

  const deviceTypeData = useMemo<DeviceTypeTree | undefined>(() => {
    return rawData ? adaptToDeviceTypeTree(rawData) : undefined;
  }, [rawData]);

  // 当弹窗打开且 ouIds 变化时，同步选中状态
  useEffect(() => {
    if (open) {
      setSelectedRowKeys(ouIds.map(id => id));
      setSelectedRow([]);
      setPageOffset(1);
    }
  }, [open, ouIds]);

  const onReset = () => {
    setSearchParams({});
    setPageOffset(1);
  };

  const onFinish = (values: searchParams) => {
    const typeId = getChildNodesByParentIds(values.typeId, deviceTypeData?.tree);

    const params: Params = {
      codeOrName: values.codeOrName,
      typeId: typeId.length > 0 ? typeId : getChildNodesByParentIds([306], deviceTypeData?.tree),
    };
    setSearchParams(params);
    setPageOffset(1);
  };
  const onSelectChange = (rowKeys: Key[], SelectedRows: DeviceListItem[]) => {
    const numericKeys = rowKeys.map(key => Number(key));
    setSelectedRow(prev => {
      const selectedKeySet = new Set(numericKeys);
      const rowMap = new Map(prev.map(item => [Number(item.id), item]));

      getValidSelectedRows(SelectedRows).forEach(item => {
        rowMap.set(Number(item.id), item);
      });

      return Array.from(rowMap.values()).filter(item => selectedKeySet.has(Number(item.id)));
    });
    setSelectedRowKeys(numericKeys);
  };
  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    getCheckboxProps: (record: DeviceListItem) => {
      const isInitial = initialSelectedSet.has(record.id as number);
      return {
        disabled: isInitial,
        name: record.name,
      };
    },
    onChange: onSelectChange,
  };
  const handleOk = () => {
    onSubmit(selectedRow);
    setSearchParams({});
    cancelModal();
  };

  const { data, isLoading }: { data?: DeviceListItem[]; isLoading?: boolean } = useQuery(
    useCallback(async () => {
      if (!deviceTypeData) return;
      const defaultIds = getChildNodesByParentIds([306], deviceTypeData.tree);

      const res = await apiV2DevicePagePost({
        ...searchParams,
        typeId: searchParams?.typeId ?? defaultIds,
        page: pageOffset,
        size: pageSize,
      });
      if (res.total) setTotalCount(res.total);
      return res.list;
    }, [pageOffset, pageSize, searchParams, setTotalCount, deviceTypeData])
  );

  return (
    <Modal title="关联设备" open={open} width={1200} onOk={handleOk} onCancel={cancelModal}>
      <CustomFilter form={form} onFinish={val => onFinish(val as searchParams)} colSpan={8} onReset={onReset}>
        <Form.Item name="codeOrName" label="编号/名称">
          <Input placeholder={'请输入编号或名称查询'} />
        </Form.Item>
        <Form.Item name="typeId" label="所属类目">
          <Cascader
            options={formatOptionData(deviceTypeData)}
            allowClear={false}
            fieldNames={{ children: 'child' }}
            multiple
            maxTagCount="responsive"
            showSearch={{
              filter: (inputValue, path) =>
                path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1),
            }}
            placeholder={'请选择所属类目'}
          />
        </Form.Item>
      </CustomFilter>
      <Table
        scroll={{ y: 380 }}
        rowKey="id"
        loading={isLoading}
        showSelectedCount
        selectedCount={selectedRowKeys?.length ?? 0}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default UnitModal;
